import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { 
  Mail, Shield, CheckCircle, XCircle, Loader2, 
  AlertTriangle, ArrowLeft, ToggleLeft, ToggleRight,
  Send, RefreshCw, Clock, Database
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GmailAdminSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [settings, setSettings] = useState(null);
  const [syncStats, setSyncStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncHours, setSyncHours] = useState(48); // Default to 48 hours
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchSettings();
    fetchSyncStats();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gmail/admin/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch Gmail settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/synced-emails/stats/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSyncStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch sync stats:', error);
    }
  };

  const toggleEnabled = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/gmail/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !settings?.enabled })
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, enabled: data.enabled }));
        setMessage({ type: 'success', text: data.message });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  const triggerManualSync = async () => {
    setSyncing(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/synced-emails/sync?hours=${syncHours}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ 
          type: 'success', 
          text: `Sync completed: ${data.emails_synced} emails synced for ${data.contacts_processed} contacts`
        });
        fetchSyncStats(); // Refresh stats
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Sync failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to trigger sync' });
    } finally {
      setSyncing(false);
    }
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-8 h-8 text-[#A2FF00]" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Gmail Integration Settings
            </h1>
          </div>
          <p className="text-gray-400">
            Manage Gmail integration for sending proposals and syncing contact emails.
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-sm border flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
              {message.text}
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#A2FF00]" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Configuration Status */}
            <div className="bg-[#111] border border-white/10 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-[#A2FF00]" />
                <h2 className="text-lg font-semibold text-white">Configuration Status</h2>
              </div>
              
              {settings?.configured ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="text-green-400 font-medium">Service Account Connected</div>
                      <div className="text-gray-400 text-sm">Domain: @{settings.workspace_domain}</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    <p>✓ Using Google Workspace Domain-Wide Delegation</p>
                    <p>✓ All @{settings.workspace_domain} users can send emails</p>
                    <p>✓ No per-user setup required</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-yellow-400 font-medium">Configuration Required</div>
                    <div className="text-gray-400 text-sm mt-1">
                      Gmail integration requires a Google Service Account with Domain-Wide Delegation.
                      Contact your system administrator to configure.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Enable/Disable Toggle */}
            <div className="bg-[#111] border border-white/10 rounded-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {settings?.enabled ? (
                    <ToggleRight className="w-6 h-6 text-[#A2FF00]" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <h2 className="text-lg font-semibold text-white">Gmail Integration</h2>
                    <p className="text-gray-400 text-sm">
                      {settings?.enabled 
                        ? 'Users can send emails via Gmail' 
                        : 'Gmail integration is disabled for all users'}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={toggleEnabled}
                  disabled={saving || !settings?.configured}
                  className={`relative w-14 h-8 rounded-full transition-colors ${
                    settings?.enabled ? 'bg-[#A2FF00]' : 'bg-gray-600'
                  } ${(!settings?.configured || saving) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                    settings?.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin absolute top-1 left-1 text-gray-600" />}
                  </div>
                </button>
              </div>
              
              {!settings?.configured && (
                <p className="text-yellow-400 text-sm mt-4">
                  Configure the service account before enabling Gmail integration.
                </p>
              )}
            </div>

            {/* Email Sync Section */}
            {settings?.configured && (
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-[#A2FF00]" />
                    <h2 className="text-lg font-semibold text-white">Email Sync</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={syncHours}
                      onChange={(e) => setSyncHours(Number(e.target.value))}
                      disabled={syncing}
                      className="bg-[#0A0A0A] border border-white/20 text-white px-3 py-2 rounded-sm text-sm"
                    >
                      <option value={24}>Last 24 hours</option>
                      <option value={48}>Last 48 hours</option>
                      <option value={168}>Last 7 days</option>
                      <option value={336}>Last 14 days</option>
                      <option value={720}>Last 30 days</option>
                    </select>
                    <button
                      onClick={triggerManualSync}
                      disabled={syncing}
                      className="flex items-center gap-2 bg-[#A2FF00] text-black px-4 py-2 rounded-sm font-bold hover:bg-[#8FE600] disabled:opacity-50 transition-colors"
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      {syncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                </div>
                
                <p className="text-gray-400 text-sm mb-4">
                  Emails are automatically synced daily at midnight Paris time. 
                  Use the button above to trigger a manual sync.
                </p>
                
                {/* Sync Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                    <div className="text-2xl font-bold text-blue-400">
                      {syncStats?.total_emails || 0}
                    </div>
                    <p className="text-gray-400 text-sm mt-1">Total Emails</p>
                  </div>
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-sm">
                    <div className="text-2xl font-bold text-purple-400">
                      {syncStats?.sent_count || 0}
                    </div>
                    <p className="text-gray-400 text-sm mt-1">Sent</p>
                  </div>
                  <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-sm">
                    <div className="text-2xl font-bold text-cyan-400">
                      {syncStats?.received_count || 0}
                    </div>
                    <p className="text-gray-400 text-sm mt-1">Received</p>
                  </div>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <div className="text-2xl font-bold text-green-400">
                      {syncStats?.unique_contacts || 0}
                    </div>
                    <p className="text-gray-400 text-sm mt-1">Contacts</p>
                  </div>
                </div>
                
                {/* Last Sync Info */}
                {syncStats?.last_sync && (
                  <div className="mt-4 flex items-center gap-2 text-gray-400 text-sm">
                    <Clock className="w-4 h-4" />
                    Last sync: {new Date(syncStats.last_sync.completed_at).toLocaleString()} 
                    ({syncStats.last_sync.stats?.emails_synced || 0} emails synced)
                  </div>
                )}
              </div>
            )}

            {/* Email Stats */}
            {settings?.configured && (
              <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Send className="w-5 h-5 text-[#A2FF00]" />
                  <h2 className="text-lg font-semibold text-white">Sending Statistics</h2>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <div className="text-3xl font-bold text-green-400">
                      {settings?.stats?.total_sent || 0}
                    </div>
                    <p className="text-gray-400 text-sm mt-1">Emails Sent</p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                    <div className="text-3xl font-bold text-red-400">
                      {settings?.stats?.total_failed || 0}
                    </div>
                    <p className="text-gray-400 text-sm mt-1">Failed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Disable Info */}
            <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-red-400 font-medium">Emergency Disable</div>
                  <p className="text-gray-400 text-sm mt-1">
                    Use the toggle above to immediately disable Gmail integration for all users in case of security concerns or issues.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default GmailAdminSettings;
